/**
 * Client-side promoter attribution helpers: promo cookie, localStorage fallback,
 * and login redirect paths. Keeps promotion tracking resilient across OIDC login.
 */

/** Cookie name set by the shortener and consumer (must match orders service expectations). */
export const PROMO_COOKIE_NAME = "promo";

/** localStorage key for opaque short-link code only (no PII). */
export const PROMO_STORAGE_KEY = "gruv_promo_code";

/** TTL aligned with consumer-refreshed promo cookie (7 days). */
export const PROMO_ATTRIBUTION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

type StoredPromoPayload = {
  code: string;
  storedAt: number;
};

/**
 * Returns the promoter short-link code from the `promo` cookie, if present.
 */
export function getPromoCodeFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === PROMO_COOKIE_NAME && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Reads a valid stored promo code, or null if missing / expired / corrupt.
 */
export function getStoredPromoCode(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PROMO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPromoPayload;
    if (!parsed?.code || typeof parsed.storedAt !== "number") {
      window.localStorage.removeItem(PROMO_STORAGE_KEY);
      return null;
    }
    if (Date.now() - parsed.storedAt > PROMO_ATTRIBUTION_MAX_AGE_MS) {
      window.localStorage.removeItem(PROMO_STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    try {
      window.localStorage.removeItem(PROMO_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
}

/**
 * Persists the opaque promo code for fallback when the cookie is unavailable.
 */
export function setStoredPromoCode(code: string): void {
  if (typeof window === "undefined") return;
  try {
    const payload: StoredPromoPayload = { code, storedAt: Date.now() };
    window.localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

/**
 * Removes stored promo attribution from localStorage.
 */
export function clearStoredPromoCode(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PROMO_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Resolves promo code: prefer cookie, then non-expired localStorage entry.
 *
 * Attribution precedence (product rule): the last successfully validated
 * promoter touch within the active attribution window wins. Setting the cookie
 * / localStorage on a successful /api/promoters/validate call overwrites any
 * previous code, so a later valid ?promoter= entry replaces the earlier one.
 */
export function resolvePromoCodeForClient(): string | null {
  return getPromoCodeFromCookie() ?? getStoredPromoCode();
}

/**
 * Clears the host-only and common domain variants of the promo cookie (best-effort).
 */
export function clearPromoCookieClient(): void {
  if (typeof document === "undefined") return;
  const domain = typeof window !== "undefined" ? window.location.hostname : "";
  const rootDomain = domain.includes(".") ? `.${domain.split(".").slice(-2).join(".")}` : domain;
  document.cookie = `${PROMO_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
  if (domain && domain !== "localhost") {
    document.cookie = `${PROMO_COOKIE_NAME}=; path=/; domain=${rootDomain}; max-age=0; SameSite=Lax`;
    document.cookie = `${PROMO_COOKIE_NAME}=; path=/; domain=${domain}; max-age=0; SameSite=Lax`;
    document.cookie = `${PROMO_COOKIE_NAME}=; path=/; domain=.${domain}; max-age=0; SameSite=Lax`;
  }
}

/**
 * Clears cookie + localStorage promo attribution.
 */
export function clearAllPromoAttributionClient(): void {
  clearPromoCookieClient();
  clearStoredPromoCode();
}

/**
 * Builds `/events/:slug` plus optional query string for OIDC returnTo / login redirect.
 *
 * @param eventSlug - Event URL slug
 * @param queryString - From `useSearchParams().toString()` (no leading `?`)
 */
export function buildLoginRedirectForEventPage(eventSlug: string, queryString: string): string {
  const q = queryString.trim();
  return q ? `/events/${eventSlug}?${q}` : `/events/${eventSlug}`;
}

/**
 * Builds the post-login destination for middleware when forcing login (pathname + search).
 */
export function buildProtectedRouteLoginRedirectTarget(pathname: string, search: string): string {
  return `${pathname}${search}`;
}

/**
 * Sets the promo cookie with default consumer flags (7-day max-age, SameSite=Lax)
 * and mirrors the code into localStorage.
 */
export function setPromoCookieClient(code: string, maxAgeSeconds: number = 7 * 24 * 60 * 60): void {
  if (typeof document === "undefined") return;
  document.cookie = `${PROMO_COOKIE_NAME}=${encodeURIComponent(code)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
  setStoredPromoCode(code);
}

/**
 * Returns true when validate API failure should remove attribution (client error / definitive invalid).
 * Does not clear on 5xx or network failures (status 0).
 */
export function shouldClearPromoOnValidateHttpStatus(status: number): boolean {
  return status > 0 && status < 500;
}

export type PromoValidateOutcome =
  | { kind: "valid"; code: string }
  | { kind: "clear" }
  | { kind: "retain" };

/**
 * Applies promoter validation response semantics.
 * - valid + matching eventId → persist cookie+storage
 * - definitive invalid / mismatch / 4xx → clear
 * - 5xx or network (status 0) → retain existing attribution
 */
export function applyPromoValidationResult(input: {
  code: string;
  eventId: string;
  ok: boolean;
  status: number;
  valid?: boolean;
  responseEventId?: string;
}): PromoValidateOutcome {
  if (input.ok && input.valid && input.responseEventId === input.eventId) {
    setPromoCookieClient(input.code);
    return { kind: "valid", code: input.code };
  }
  if (!input.ok && !shouldClearPromoOnValidateHttpStatus(input.status)) {
    return { kind: "retain" };
  }
  // Definitive invalid (including ok:true but mismatch) or 4xx
  if (!input.ok || !input.valid || input.responseEventId !== input.eventId) {
    clearAllPromoAttributionClient();
    return { kind: "clear" };
  }
  return { kind: "retain" };
}
